/**
 * Kiosk imprint slots (12 §2) — the pure cell model behind the hub loadout menu's slot rows. The server
 * (Progression.setSlot) is authoritative; this only decides what to show and how to label it.
 */
import { POWERUP_POOL, overlaysFor, powerUpById } from '../../../sim/powerups.js'
import { KIT_UNLOCKS, SLOT_LEVELS, clashingKeys } from '../../../shared/progression.js'
import type { DisclordView } from '../../../shared/protocol.js'

export type KioskCellState = 'slotted' | 'available' | 'incompatible' | 'locked'
export interface KioskCell { id: string; name: string; glyph: string | null; state: KioskCellState; clashing: string[]; unlockLevel?: number; /** Unlocked but without tuning overlays yet (12 §3 values pending): slotting it does nothing. */ inert?: boolean }
export type KioskSlot = { index: 2 | 3; locked: true; opensAt: number } | { index: 2 | 3; locked: false; current: string | null; cells: KioskCell[] }

const NEW_NAMES: Record<string, string> = { intercept: 'Intercept', curveball: 'Curveball', anchor: 'Anchor', ricochet_sense: 'Ricochet Sense', afterimage: 'Afterimage', magnet: 'Magnet', plated: 'Plated', overclock: 'Overclock' }

/** One slot row: locked (with the level it opens at) or its cells — kit first, then still-locked kit with unlock levels. */
export function kioskSlot(view: DisclordView, index: 2 | 3): KioskSlot {
  if (index > view.slotsOpen) return { index, locked: true, opensAt: SLOT_LEVELS[index - 1] }
  const current = view.slots[index - 1] ?? null
  const other = view.slots[index === 2 ? 2 : 1] ?? null
  const cells: KioskCell[] = []
  for (const p of POWERUP_POOL) {
    if (!view.kit.includes(p.id)) continue
    const clashing = other && other !== p.id ? clashingKeys(p.id, other) : []
    cells.push({ id: p.id, name: p.name, glyph: p.glyph, state: p.id === current ? 'slotted' : clashing.length ? 'incompatible' : 'available', clashing, inert: false })
  }
  for (const id of view.kit) if (!POWERUP_POOL.some((p) => p.id === id)) { // Phase 2 unlocks not in the live pool yet
    const clashing = other && other !== id ? clashingKeys(id, other) : []
    cells.push({ id, name: powerUpById(id)?.name ?? NEW_NAMES[id] ?? id, glyph: powerUpById(id)?.glyph ?? null, state: id === current ? 'slotted' : clashing.length ? 'incompatible' : 'available', clashing, inert: Object.keys(overlaysFor([id])[0] ?? {}).length === 0 })
  }
  for (const [id, lvl] of Object.entries(KIT_UNLOCKS)) if (!view.kit.includes(id)) cells.push({ id, name: NEW_NAMES[id] ?? id, glyph: powerUpById(id)?.glyph ?? null, state: 'locked', clashing: [], unlockLevel: lvl })
  return { index, locked: false, current, cells }
}
